import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { syllabusData } from '@/mocks/syllabus';
import type { ExamBoard, SyllabusTopic, SubTopic } from '@/mocks/syllabus';
import { FilterBar, FilterPills, FilterSearch } from '@/components/feature/FilterBar';

const GCSE_BOARDS: ExamBoard[] = ['AQA', 'OCR', 'Edexcel', 'WJEC'];

function TopicCard({ topic, searchQuery }: { topic: SyllabusTopic; searchQuery: string }) {
  const [expandedSub, setExpandedSub] = useState<string | null>(null);

  const filteredSubs = searchQuery.trim()
    ? topic.subtopics.filter(
        (s) =>
          s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : topic.subtopics;

  if (filteredSubs.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-emerald-100 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 bg-emerald-50 border-b border-emerald-100">
        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/70 flex-shrink-0">
          <i className={`${topic.icon} text-emerald-600 text-base`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-xs font-bold uppercase tracking-wider text-emerald-600 opacity-70">{topic.unit}</span>
          <h3 className="text-sm font-bold text-slate-800 leading-tight">{topic.title}</h3>
        </div>
        <span className="text-xs font-medium text-slate-500 bg-white/60 px-2 py-0.5 rounded-full flex-shrink-0">
          {filteredSubs.length} subtopics
        </span>
      </div>
      <ul className="divide-y divide-slate-50">
        {filteredSubs.map((sub: SubTopic) => (
          <li key={sub.id}>
            <button
              onClick={() => setExpandedSub(expandedSub === sub.id ? null : sub.id)}
              className="w-full text-left px-5 py-3 group cursor-pointer hover:bg-slate-50 transition-colors flex items-start gap-3"
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`ri-arrow-right-s-line text-slate-300 text-sm group-hover:text-slate-500 transition-all ${expandedSub === sub.id ? 'rotate-90' : ''}`}></i>
              </div>
              <span className="text-sm text-slate-700 group-hover:text-slate-900 transition-colors leading-snug">{sub.title}</span>
            </button>
            {expandedSub === sub.id && (
              <div className="px-5 pb-4 ml-7">
                <p className="text-xs text-slate-500 leading-relaxed mb-3">{sub.description}</p>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                    sub.difficulty === 'Foundation' ? 'bg-emerald-100 text-emerald-700' :
                    sub.difficulty === 'Higher' ? 'bg-orange-100 text-orange-700' :
                    'bg-sky-100 text-sky-700'
                  }`}>{sub.difficulty}</span>
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <i className="ri-time-line text-xs"></i>{sub.estimatedMins} min
                  </span>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function GcseTopicsPage() {
  const [selectedBoard, setSelectedBoard] = useState<ExamBoard>('AQA');
  const [searchQuery, setSearchQuery] = useState('');

  const syllabus = useMemo(
    () => syllabusData.find((s) => s.board === selectedBoard && s.level === 'GCSE' && s.subject === 'Computer Science'),
    [selectedBoard]
  );

  const filteredTopics = useMemo(() => {
    if (!syllabus) return [];
    if (!searchQuery.trim()) return syllabus.topics;
    return syllabus.topics.filter(
      (t) =>
        t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.subtopics.some(
          (s) =>
            s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            s.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
    );
  }, [syllabus, searchQuery]);

  const totalSubtopics = syllabus?.topics.reduce((a, t) => a + t.subtopics.length, 0) ?? 0;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-slate-900 pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20technology%20background%20with%20glowing%20circuit%20patterns%2C%20deep%20navy%20gradient%2C%20geometric%20grid%20lines%2C%20futuristic%20digital%20aesthetic%2C%20clean%20minimal%20design%2C%20high%20contrast%20teal%20accents%20on%20dark%20background&width=1440&height=320&seq=gcsetopics1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">GCSE Resources</span>
            <i className="ri-arrow-right-s-line text-slate-500 text-xs"></i>
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Topics</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            GCSE Computer Science Topics
          </h1>
          <p className="text-slate-400 text-base max-w-2xl mb-6">
            Browse the complete GCSE Computer Science syllabus. Select your exam board below to see every unit and subtopic with detailed explanations.
          </p>
          <div className="flex flex-wrap gap-4">
            <div className="bg-slate-800/70 rounded-lg px-4 py-2.5 flex items-center gap-2">
              <i className="ri-book-open-line text-emerald-400 text-sm"></i>
              <span className="text-white text-sm font-medium">{syllabus?.topics.length ?? 0} Units</span>
            </div>
            <div className="bg-slate-800/70 rounded-lg px-4 py-2.5 flex items-center gap-2">
              <i className="ri-list-check text-emerald-400 text-sm"></i>
              <span className="text-white text-sm font-medium">{totalSubtopics} Subtopics</span>
            </div>
            <div className="bg-slate-800/70 rounded-lg px-4 py-2.5 flex items-center gap-2">
              <i className="ri-file-text-line text-emerald-400 text-sm"></i>
              <span className="text-white text-sm font-medium">Spec {syllabus?.specCode}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Bar */}
      <FilterBar>
        <FilterPills
          label="Exam Board"
          value={selectedBoard}
          onChange={(v) => { setSelectedBoard(v as ExamBoard); setSearchQuery(''); }}
          options={GCSE_BOARDS.map((board) => ({ value: board, label: board }))}
        />
        <div className="sm:ml-auto">
          <FilterSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search topics or subtopics..."
            width="sm:w-72"
          />
        </div>
      </FilterBar>

      {/* Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 md:px-6 py-10 w-full">
        {/* Board info banner */}
        {syllabus && (
          <div className="flex items-center gap-4 p-4 rounded-xl border border-emerald-100 mb-8 bg-emerald-50">
            <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
              <i className="ri-award-line text-emerald-600 text-lg"></i>
            </div>
            <div className="flex-1">
              <p className="font-bold text-sm text-emerald-700">{syllabus.board} GCSE Computer Science — Specification {syllabus.specCode}</p>
              <p className="text-slate-500 text-xs mt-0.5">Paper 1: {syllabus.paper1} &nbsp;·&nbsp; Paper 2: {syllabus.paper2}</p>
            </div>
            <div className="hidden sm:flex items-center gap-3 text-xs text-slate-500">
              <span className="bg-white px-3 py-1 rounded-full border border-slate-200 font-medium">{syllabus.topics.length} units</span>
              <span className="bg-white px-3 py-1 rounded-full border border-slate-200 font-medium">{totalSubtopics} subtopics</span>
            </div>
          </div>
        )}

        {/* Search results info */}
        {searchQuery && (
          <p className="text-sm text-slate-500 mb-5">
            Showing results for <span className="font-semibold text-slate-700">"{searchQuery}"</span> — {filteredTopics.length} unit{filteredTopics.length !== 1 ? 's' : ''} found
          </p>
        )}

        {filteredTopics.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-2xl mb-4">
              <i className="ri-search-line text-slate-400 text-2xl"></i>
            </div>
            <p className="text-slate-600 font-medium mb-1">No topics found</p>
            <p className="text-slate-400 text-sm">Try a different search term or select a different exam board</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredTopics.map((topic) => (
              <TopicCard key={topic.id} topic={topic} searchQuery={searchQuery} />
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
